import { useRef, useState } from 'react';

import { useLayoutEffect } from './useLayoutEffect';
import { focusCellOrCellContent } from '../utils';
import type { CalculatedColumn, Position } from '../types';

export interface SelectCellState extends Position {
  readonly mode: 'SELECT';
}

export interface EditCellState<R> extends Position {
  readonly mode: 'EDIT';
  readonly row: R;
  readonly originalRow: R;
}

export const initialSelectedPosition: SelectCellState = {
  idx: -1,
  // use -Infinity to avoid issues when adding header rows or top summary rows
  rowIdx: Number.NEGATIVE_INFINITY,
  mode: 'SELECT',
};

export function useSelectedPosition<R, SR>({
  gridRef,
  columns,
  rows,
  maxColIdx,
  minRowIdx,
  maxRowIdx,
  setDraggedOverRowIdx,
}: {
  gridRef: React.RefObject<HTMLDivElement>;
  columns: readonly CalculatedColumn<R, SR>[];
  rows: readonly R[];
  maxColIdx: number;
  minRowIdx: number;
  maxRowIdx: number;
  setDraggedOverRowIdx: (rowIdx: number | undefined) => void;
}) {
  const [selectedPosition, setSelectedPosition] = useState<SelectCellState | EditCellState<R>>(initialSelectedPosition);
  const [positionToFocus, setPositionToFocus] = useState<SelectCellState | EditCellState<R> | null>(null);
  const positionToFocusRef = useRef<SelectCellState | EditCellState<R> | null>(null);

  /**
   * Returns whether the given position represents a valid cell position in the grid.
   * Selection bounds: any valid position in the grid
   * Viewport bounds: any valid position in the grid outside of header rows and summary rows
   */
  function validatePosition({ idx, rowIdx }: Position) {
    const isColIdxInSelectionBounds = idx >= 0 && idx <= maxColIdx;
    const isRowIdxInSelectionBounds = rowIdx >= minRowIdx && rowIdx <= maxRowIdx;
    const isRowIdxInViewportBounds = rowIdx >= 0 && rowIdx < rows.length;

    return {
      isCellInSelectionBounds: isColIdxInSelectionBounds && isRowIdxInSelectionBounds,
      isCellInViewportBounds: isColIdxInSelectionBounds && isRowIdxInViewportBounds,
    };
  }

  function getResolvedValues(position: SelectCellState | EditCellState<R>) {
    return {
      resolvedSelectedPosition: position,
      validatedPosition: validatePosition(position),
    };
  }

  function getSelectedColumn(): CalculatedColumn<R, SR> {
    if (!validatedPosition.isCellInSelectionBounds) {
      throw new Error('No column for selected position');
    }
    return columns[resolvedSelectedPosition.idx];
  }

  function getSelectedRow(): R {
    if (!validatedPosition.isCellInViewportBounds) {
      throw new Error('No row for selected position');
    }
    return rows[resolvedSelectedPosition.rowIdx];
  }

  let { resolvedSelectedPosition, validatedPosition } = getResolvedValues(selectedPosition);

  // Reinitialize the selected position and immediately use the new state if it is no longer valid.
  // This can happen when a column or row is removed.
  if (!validatedPosition.isCellInSelectionBounds && resolvedSelectedPosition !== initialSelectedPosition) {
    setSelectedPosition(initialSelectedPosition);
    setDraggedOverRowIdx(undefined);
    ({ resolvedSelectedPosition, validatedPosition } = getResolvedValues(initialSelectedPosition));
  } else if (
    resolvedSelectedPosition.mode === 'EDIT' &&
    validatedPosition.isCellInViewportBounds &&
    getSelectedRow() !== resolvedSelectedPosition.originalRow
  ) {
    // The row was updated from outside: refresh the editor row instead of closing the editor,
    // since we might keep editors open
    const row = getSelectedRow();
    const newPosition: EditCellState<R> = { ...resolvedSelectedPosition, row, originalRow: row };
    setSelectedPosition(newPosition);
    ({ resolvedSelectedPosition, validatedPosition } = getResolvedValues(newPosition));
  }

  useLayoutEffect(() => {
    // Layout effects clean up when the component is replaced by a suspense fallback,
    // then re-mount when the suspense boundary resolves.
    // So we use a ref to:
    // 1. avoid re-focusing after the effect re-mounts
    // 2. avoid re-rendering by not re-setting the state
    if (positionToFocus !== null && positionToFocus !== positionToFocusRef.current) {
      positionToFocusRef.current = positionToFocus;
      focusCellOrCellContent(gridRef.current!);
    }
  }, [positionToFocus, gridRef]);

  return {
    selectedPosition: resolvedSelectedPosition,
    setSelectedPosition,
    setPositionToFocus,
    selectedCellIsWithinSelectionBounds: validatedPosition.isCellInSelectionBounds,
    selectedCellIsWithinViewportBounds: validatedPosition.isCellInViewportBounds,
    validatePosition,
    getSelectedColumn,
    getSelectedRow,
  } as const;
}
